import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjects } from '../contexts/ProjectContext';
import { useRealtime } from '../contexts/RealtimeContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { TerminalView } from '../components/terminal/TerminalView';
import { RunScriptModal } from '../components/modals/RunScriptModal';
import { ConfirmDeployModal } from '../components/modals/ConfirmDeployModal';
import { 
  FolderKanban, 
  Play, 
  CloudUpload, 
  Eye, 
  Terminal, 
  Files, 
  Bot, 
  Settings, 
  ShieldCheck, 
  GitBranch,
  ArrowLeft
} from 'lucide-react';
import { ScriptCandidate } from '@kaizen/shared';

export const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { sendJob, device } = useRealtime();
  const { displayPath } = usePrivacy();

  const project = projects.find(p => p.id === id) || projects[0];

  const [activeTab, setActiveTab] = useState<'console' | 'assistant' | 'files' | 'deployments' | 'settings'>('console');
  const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState<ScriptCandidate | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (!project) {
    return <div className="p-8 text-center text-slate-500">Project not found.</div>;
  }


  const deployment =
    project.config?.deployment;


  const isVercelRender =
    deployment?.mode ===
      'vercel_render';


  const createProductionDeployPayload = () => {

    if (
      isVercelRender
    ) {

      return {
        projectName:
          project.name,

        targetEnvironment:
          'production' as const,

        deploymentMode:
          'vercel_render' as const,

        gitRemote:
          deployment?.gitRemote ||
          'origin',

        gitBranch:
          deployment?.gitBranch ||
          'main',

        vercelTeamId:
          deployment?.vercel?.teamId,

        vercelProjectId:
          deployment?.vercel?.projectId,

        vercelProductionUrl:
          deployment?.vercel?.productionUrl,

        renderServiceId:
          deployment?.render?.serviceId,

        renderHealthUrl:
          deployment?.render?.healthUrl,

        renderApiKeyEnv:
          deployment?.render?.apiKeyEnv ||
          'RENDER_API_KEY'
      };
    }


    return {
      projectName:
        project.cloudflareProjectName ||
        'production-project',

      targetEnvironment:
        'production' as const,

      deploymentMode:
        'cloudflare_pages' as const,

      args: [
        project.buildOutputDirectory
      ]
    };
  };


  const handleTriggerBuild = async () => {
    setActionError(null);
    try {
      await sendJob({
        deviceId: device?.id || '11111111-1111-1111-1111-111111111111',
        projectId: project.id,
        type: 'build',
        payload: {
          command: project.buildCommand
        },
        workingDirectory: project.localPath
      });
      setActiveTab('console');
    } catch (err: any) {
      console.error('Build dispatch failed:', err);
      setActionError(`Build dispatch failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleTriggerPreviewDeploy = async () => {
    setActionError(null);

    if (
      isVercelRender ||
      !project.previewEnabled
    ) {
      setActionError(
        'Preview deployment is not configured for this project.'
      );

      return;
    }

    try {
      await sendJob({
        deviceId: device?.id || '11111111-1111-1111-1111-111111111111',
        projectId: project.id,
        type: 'deploy_preview',
        payload: {
          projectName: project.cloudflareProjectName || 'preview-project',
          args: [project.buildOutputDirectory]
        },
        workingDirectory: project.localPath
      });
      setActiveTab('console');
    } catch (err: any) {
      console.error('Preview deploy failed:', err);
      setActionError(`Preview deploy failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleTriggerProductionDeploy = async () => {
    setActionError(null);
    try {
      await sendJob({
        deviceId: device?.id || '11111111-1111-1111-1111-111111111111',
        projectId: project.id,
        type: 'deploy_production',
        payload:
          createProductionDeployPayload(),
        workingDirectory: project.localPath
      });
      setActiveTab('console');
    } catch (err: any) {
      console.error('Production deploy failed:', err);
      setActionError(`Production deploy failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleOpenScriptPrompt = () => {
    setSelectedScript({
      id: `custom-script-${Date.now()}`,
      filename: 'CUSTOM_EXECUTION.ps1',
      type: 'powershell_command',
      size: 40,
      source: 'Project Action Bar',
      receivedTime: new Date().toISOString(),
      status: 'pending',
      command: 'npm run test',
      workingDirectory: project.localPath
    });
    setIsScriptModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Back Button & Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/projects')}
          className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-slate-900">{project.name}</h2>
            <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
              {project.projectType}
            </span>
          </div>
          <p className="text-xs text-slate-500 font-mono mt-0.5">
            {displayPath(project.localPath)}
          </p>
        </div>
      </div>

      {actionError && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-center justify-between">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-rose-500 hover:text-rose-700 font-bold ml-2">×</button>
        </div>
      )}

      {/* Prominent Action Bar */}
      <div className="acrylic-panel p-3 sm:p-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenScriptPrompt}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-subtle transition-colors"
          >
            <Terminal className="w-3.5 h-3.5 text-slate-600" />
            <span>RUN SCRIPT</span>
          </button>
          <button
            onClick={handleTriggerBuild}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-colors"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
            <span>BUILD</span>
          </button>
          <button
            onClick={handleTriggerPreviewDeploy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-indigo-700 hover:bg-indigo-50 shadow-subtle transition-colors"
          >
            <Eye className="w-3.5 h-3.5 text-indigo-600" />
            <span>PREVIEW DEPLOY</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsDeployModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors"
          >
            <CloudUpload className="w-3.5 h-3.5 text-amber-400" />
            <span>DEPLOY PRODUCTION</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-5 md:gap-6 text-xs font-medium overflow-x-auto scrollbar-none">
        <button
          onClick={() => setActiveTab('console')}
          className={`pb-3 border-b-2 flex items-center gap-1.5 ${
            activeTab === 'console'
              ? 'border-indigo-600 text-indigo-600 font-semibold'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Terminal className="w-3.5 h-3.5" />
          <span>Console</span>
        </button>
        <button
          onClick={() => setActiveTab('assistant')}
          className={`pb-3 border-b-2 flex items-center gap-1.5 ${
            activeTab === 'assistant'
              ? 'border-indigo-600 text-indigo-600 font-semibold'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Bot className="w-3.5 h-3.5" />
          <span>JARVIS</span>
        </button>
        <button
          onClick={() => setActiveTab('files')}
          className={`pb-3 border-b-2 flex items-center gap-1.5 ${
            activeTab === 'files'
              ? 'border-indigo-600 text-indigo-600 font-semibold'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Files className="w-3.5 h-3.5" />
          <span>Files</span>
        </button>
        <button
          onClick={() => setActiveTab('deployments')}
          className={`pb-3 border-b-2 flex items-center gap-1.5 ${
            activeTab === 'deployments'
              ? 'border-indigo-600 text-indigo-600 font-semibold'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <CloudUpload className="w-3.5 h-3.5" />
          <span>Deployments</span>
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`pb-3 border-b-2 flex items-center gap-1.5 ${
            activeTab === 'settings'
              ? 'border-indigo-600 text-indigo-600 font-semibold'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Settings className="w-3.5 h-3.5" />
          <span>Settings</span>
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === 'console' && (
        <TerminalView title={`${project.name} Terminal`} maxHeight="h-[460px]" />
      )}

      {activeTab === 'assistant' && (
        <div className="acrylic-panel p-6 text-center space-y-3">
          <Bot className="w-8 h-8 text-indigo-500 mx-auto" />
          <h3 className="font-bold text-sm text-slate-800">Project JARVIS Context</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Context and scripts related specifically to {project.name} will be tracked here. Go to the primary Assistant view to send prompts.
          </p>
          <button
            onClick={() => navigate('/assistant')}
            className="px-3.5 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg"
          >
            Open JARVIS
          </button>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="acrylic-panel p-6 space-y-4">
          <h3 className="font-bold text-sm text-slate-800">Generated Reports & Build Artifacts</h3>
          <p className="text-xs text-slate-500">
            Reports generated by PowerShell jobs in {displayPath(project.localPath)} are surfaced in the Files catalog.
          </p>
          <button
            onClick={() => navigate('/files')}
            className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
          >
            Open Global Files View
          </button>
        </div>
      )}

      {activeTab === 'deployments' && (
        <div className="acrylic-panel p-6 space-y-3">
          <h3 className="font-bold text-sm text-slate-800">Deployment History for {project.name}</h3>
          <p className="text-xs text-slate-500">
            Deployment:
            {' '}
            <span className="font-mono text-slate-700">
              {isVercelRender
                ? 'Vercel frontend + Render backend'
                : (
                    project.cloudflareProjectName ||
                    'Not configured'
                  )}
            </span>
          </p>
          <button
            onClick={() => navigate('/deployments')}
            className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
          >
            Open Deployments View
          </button>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="acrylic-panel p-6 space-y-4 max-w-xl text-xs">
          <h3 className="font-bold text-sm text-slate-800">Project Profile Configuration</h3>
          <div className="space-y-2">
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Build Command:</span>
              <span className="font-mono font-semibold text-slate-800">{project.buildCommand}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Test Command:</span>
              <span className="font-mono text-slate-800">{project.testCommand || 'None'}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Build Output Directory:</span>
              <span className="font-mono text-slate-800">{project.buildOutputDirectory}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500">Production Protected:</span>
              <span className="text-emerald-700 font-semibold">{project.productionEnabled ? 'Yes (Requires Confirmation)' : 'No'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Script Approval Gate */}
      <RunScriptModal
        script={selectedScript}
        workingDirectory={project.localPath}
        projectName={project.name}
        isOpen={isScriptModalOpen}
        onClose={() => setIsScriptModalOpen(false)}
        onConfirm={async (s) => {
          setIsScriptModalOpen(false);
          setActionError(null);
          try {
            await sendJob({
              deviceId: device?.id || '11111111-1111-1111-1111-111111111111',
              projectId: project.id,
              type: 'powershell_command',
              payload: { command: s.command },
              workingDirectory: project.localPath
            });
            setActiveTab('console');
          } catch (err: any) {
            console.error('Custom script execution failed:', err);
            setActionError(`Execution failed: ${err?.message || 'Unknown error'}`);
          }
        }}
      />

      {/* Confirm Production Deploy Modal */}
      <ConfirmDeployModal
        project={project}
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
        onConfirm={handleTriggerProductionDeploy}
      />
    </div>
  );
};
